<template>
  <div class="w-full">
    <label
      :for="disabled ? 'disabled' + question.id : question.id"
      class="block w-full text-sm font-semibold leading-6 text-gray-800 sm:w-auto"
      >{{ question.title }}</label
    >
    <div class="mt-2">
      <textarea
        rows="4"
        :name="disabled ? 'disabled' + question.id : question.id"
        :id="disabled ? 'disabled' + question.id : question.id"
        v-model="data"
        :disabled="disabled"
        @blur="v$.model.$touch"
        class="block px-3 py-3 w-full text-gray-700 rounded-md border-0 ring-1 ring-inset ring-gray-300 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-theme-400 focus:ring-inset sm:text-sm sm:leading-6 focus-visible:outline-none"
        :class="{
          'border-red-500 ring-red-500': v$.model.$error && v$.model.$dirty,
        }"></textarea>
    </div>
    <p v-if="v$.model.$error && v$.model.$dirty" class="mt-1 text-xs text-red-600">
      {{ firstError }}
    </p>
  </div>
</template>

<script setup lang="ts">
import type { QstQuestionDetails } from '@/types/Questionnaires';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const data = defineModel<string>();

const props = withDefaults(
  defineProps<{
    question: QstQuestionDetails;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  }
);
const { t: $t } = useI18n({ useScope: 'global' });

function isValidRegex(pattern?: string | null): RegExp | null {
  if (!pattern?.trim()) return null;
  try {
    return new RegExp(`^${pattern}$`);
  } catch {
    return null;
  }
}
const maskRegex = isValidRegex(props.question.mwDataType.mask);

const rules = computed(() => {
  const record: Record<string, any> = {};

  if (!props.question.allowNull) {
    record.required = helpers.withMessage($t('messages.errors.requiredField'), required);
  }

  if (maskRegex) {
    record.mask = helpers.withMessage(
      $t('messages.errors.invalidValue'),
      (v: unknown) => v == null || maskRegex.test(String(v))
    );
  }

  return { model: record };
});

const v$ = useVuelidate(rules, { model: data }, { $lazy: true });

const firstError = computed(() => {
  const errs = v$.value.model.$errors;
  return errs.length ? errs[0].$message : $t('messages.errors.invalidValue');
});
</script>
